import { useState, FormEvent } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../../stores/authStore';

interface ProfileSettingsProps {
  onClose?: () => void;
}

export function ProfileSettings({ onClose }: ProfileSettingsProps) {
  const { t } = useTranslation();
  const { user, updateProfile, changePassword, logout, isLoading, error, clearError } = useAuthStore();
  const [activeTab, setActiveTab] = useState<'profile' | 'password'>('profile');
  
  // Profile tab state
  const [email, setEmail] = useState(user?.email || '');
  const [username, setUsername] = useState(user?.username || '');
  
  // Password tab state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [localError, setLocalError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const handleUpdateProfile = async (e: FormEvent) => {
    e.preventDefault();
    clearError();
    setLocalError('');
    setSuccessMessage('');

    try {
      await updateProfile({ email, username });
      setSuccessMessage(t('auth.successProfileUpdated'));
    } catch (error) {
      // Error is handled by the store
    }
  };

  const handleChangePassword = async (e: FormEvent) => {
    e.preventDefault();
    clearError();
    setLocalError('');
    setSuccessMessage('');

    if (newPassword !== confirmNewPassword) {
      setLocalError(t('auth.errorNewPasswordMismatch'));
      return;
    }

    if (newPassword.length < 6) {
      setLocalError(t('auth.errorNewPasswordLength'));
      return;
    }

    try {
      await changePassword(currentPassword, newPassword);
      setSuccessMessage(t('auth.successPasswordChanged'));
      setCurrentPassword('');
      setNewPassword('');
      setConfirmNewPassword('');
    } catch (error) {
      // Error is handled by the store
    }
  };

  const handleLogout = () => {
    logout();
    onClose?.();
  };

  const displayError = localError || error;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, y: 20 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.95, y: 20 }}
        className="w-full max-w-2xl bg-surface dark:bg-surface rounded-lg shadow-xl overflow-hidden border border-border"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <h2 className="text-2xl font-bold text-text-primary">
            {t('auth.profileSettings')}
          </h2>
          <button
            onClick={onClose}
            className="text-text-secondary hover:text-text-primary text-2xl"
          >
            ×
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-border">
          <button
            onClick={() => {
              setActiveTab('profile');
              clearError();
              setLocalError('');
              setSuccessMessage('');
            }}
            className={`flex-1 py-3 px-6 font-medium transition-colors ${
              activeTab === 'profile'
                ? 'text-primary border-b-2 border-primary'
                : 'text-text-secondary hover:text-text-primary'
            }`}
          >
            {t('auth.profileTab')}
          </button>
          <button
            onClick={() => {
              setActiveTab('password');
              clearError();
              setLocalError('');
              setSuccessMessage('');
            }}
            className={`flex-1 py-3 px-6 font-medium transition-colors ${
              activeTab === 'password'
                ? 'text-primary border-b-2 border-primary'
                : 'text-text-secondary hover:text-text-primary'
            }`}
          >
            {t('auth.passwordTab')}
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {displayError && (
            <div className="mb-4 p-3 bg-danger/10 border border-danger/30 text-danger rounded">
              {displayError}
            </div>
          )}

          {successMessage && (
            <div className="mb-4 p-3 bg-success/10 border border-success/30 text-success rounded">
              {successMessage}
            </div>
          )}

          <AnimatePresence mode="wait">
            {activeTab === 'profile' ? (
              <motion.form
                key="profile"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                onSubmit={handleUpdateProfile}
                className="space-y-4"
              >
                <div>
                  <label
                    htmlFor="email"
                    className="block text-sm font-medium text-text-primary mb-1"
                  >
                    {t('auth.email')}
                  </label>
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="w-full px-4 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent bg-surface-elevated text-text-primary"
                  />
                </div>

                <div>
                  <label
                    htmlFor="username"
                    className="block text-sm font-medium text-text-primary mb-1"
                  >
                    {t('auth.username')}
                  </label>
                  <input
                    id="username"
                    type="text"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    required
                    minLength={3}
                    className="w-full px-4 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent bg-surface-elevated text-text-primary"
                  />
                </div>

                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full py-2 px-4 bg-primary hover:bg-primary/90 disabled:bg-primary/50 text-white font-medium rounded-lg transition-colors"
                >
                  {isLoading ? t('auth.saving') : t('auth.saveProfile')}
                </button>
              </motion.form>
            ) : (
              <motion.form
                key="password"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                onSubmit={handleChangePassword}
                className="space-y-4"
              >
                <div>
                  <label
                    htmlFor="currentPassword"
                    className="block text-sm font-medium text-text-primary mb-1"
                  >
                    {t('auth.currentPassword')}
                  </label>
                  <input
                    id="currentPassword"
                    type="password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    required
                    className="w-full px-4 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent bg-surface-elevated text-text-primary"
                  />
                </div>

                <div>
                  <label
                    htmlFor="newPassword"
                    className="block text-sm font-medium text-text-primary mb-1"
                  >
                    {t('auth.newPassword')}
                  </label>
                  <input
                    id="newPassword"
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    minLength={6}
                    className="w-full px-4 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent bg-surface-elevated text-text-primary"
                  />
                </div>

                <div>
                  <label
                    htmlFor="confirmNewPassword"
                    className="block text-sm font-medium text-text-primary mb-1"
                  >
                    {t('auth.newPasswordConfirm')}
                  </label>
                  <input
                    id="confirmNewPassword"
                    type="password"
                    value={confirmNewPassword}
                    onChange={(e) => setConfirmNewPassword(e.target.value)}
                    required
                    minLength={6}
                    className="w-full px-4 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent bg-surface-elevated text-text-primary"
                  />
                </div>

                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full py-2 px-4 bg-primary hover:bg-primary/90 disabled:bg-primary/50 text-white font-medium rounded-lg transition-colors"
                >
                  {isLoading ? t('auth.changing') : t('auth.changePassword')}
                </button>
              </motion.form>
            )}
          </AnimatePresence>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-border">
          <button
            onClick={handleLogout}
            className="w-full py-2 px-4 bg-danger hover:bg-danger/90 text-white font-medium rounded-lg transition-colors"
          >
            {t('auth.logout')}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}
